import {
  isNodeShown,
  type ChangeMark,
  type ChangeSlotColumn,
  type ChangeSlots,
  type CompiledCell,
  type CompiledExtraRow,
  type CompiledFieldNode,
  type CompiledForm,
  type CompiledNode,
  type CompiledStaticNode,
  type CompiledTableNode,
  type FieldValue,
  type ResponseMarks,
} from '@record-fill';
import { formatChangeValue } from '@/features/FormBuilder';
import type {
  AuthorOf,
  CorrectedWord,
  FillLogsBefore,
  FillLogsBlock,
  FillLogsChangeItem,
  FillLogsFieldEntry,
  FillLogsReading,
  FillLogsSection,
  FillLogsTableRow,
  FillLogsText,
  FillLogsValue,
} from './fill-logs-text.types';

export type * from './fill-logs-text.types';

export interface FillLogsTextOptions {
  deviceTitles?: boolean;
}

interface Walk {
  marks: ResponseMarks;
  authorOf: AuthorOf;
  deviceTitles: boolean;
  changes: FillLogsChangeItem[];
  sections: FillLogsSection[];
}

interface RowCell {
  columnIndex: number;
  node: CompiledFieldNode;
  mark: ChangeMark | null;
}

const EMPTY_TEXT = 'vazio';
// A longer fixed text is an instruction, not the name of a block of the form.
const HEADING_MAX_LENGTH = 80;
const WIDE_VALUE_LENGTH = 60;

function isBlank(value: FieldValue | undefined): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  return Array.isArray(value) && value.length === 0;
}

export function fieldValue(node: CompiledFieldNode, value: FieldValue): FillLogsValue {
  return isBlank(value) ? { text: EMPTY_TEXT, empty: true } : { text: formatChangeValue(node, value), empty: false };
}

function ownLabel(node: CompiledFieldNode): string | null {
  const label = node.element.properties.label?.trim();
  return label ? label : null;
}

// The compiled label falls back to the field id, which no reader understands, so the tooltip comes
// first and the id stays the last resort.
function fallbackLabel(node: CompiledFieldNode): string {
  const tooltip = node.element.properties.tooltip?.trim();
  return tooltip ? tooltip : node.label;
}

function beforeOf(node: CompiledFieldNode, mark: ChangeMark | null, authorOf: AuthorOf): FillLogsBefore | null {
  if (mark?.kind !== 'corrected') return null;
  return { ...fieldValue(node, mark.before), origin: `(resposta ${mark.fromResponse}, ${authorOf(mark.fromResponse)})` };
}

function readingOf(node: CompiledFieldNode, mark: ChangeMark | null, walk: Walk): FillLogsReading {
  return { key: node.id, value: fieldValue(node, node.value), tone: mark?.kind ?? null, before: beforeOf(node, mark, walk.authorOf) };
}

function pushChange(walk: Walk, node: CompiledFieldNode, mark: ChangeMark, label: string, context: string | null): void {
  walk.changes.push({
    key: node.id,
    tone: mark.kind,
    context,
    label,
    before: beforeOf(node, mark, walk.authorOf),
    now: fieldValue(node, node.value),
    cells: [],
    targets: [node.id],
  });
}

function openSection(walk: Walk, title: string | null): FillLogsSection {
  const section = { key: `section-${walk.sections.length}`, title, blocks: [] };
  walk.sections.push(section);
  return section;
}

function pushBlock(walk: Walk, block: FillLogsBlock): void {
  const section = walk.sections.at(-1) ?? openSection(walk, null);
  section.blocks.push(block);
}

function pushEntry(walk: Walk, entry: FillLogsFieldEntry): void {
  const last = walk.sections.at(-1)?.blocks.at(-1);
  if (last?.kind === 'fields') last.entries.push(entry);
  else pushBlock(walk, { kind: 'fields', key: `fields-${entry.key}`, entries: [entry] });
}

function visitField(node: CompiledFieldNode, label: string, walk: Walk): void {
  if (!node.state.visible) return;
  const mark = walk.marks.values[node.id] ?? null;
  const reading = readingOf(node, mark, walk);
  const wide = node.type === 'PARAGRAPH' || reading.value.text.length > WIDE_VALUE_LENGTH;
  pushEntry(walk, { ...reading, label, wide });
  if (mark) pushChange(walk, node, mark, label, null);
}

export function isDeviceTitle(node: CompiledNode | null): node is CompiledStaticNode {
  return node?.kind === 'static' && (node.type === 'INDEXER_TITLE' || node.type === 'FILL_TITLE');
}

function titleValue(text: string): FillLogsValue {
  return text.trim().length === 0 ? { text: EMPTY_TEXT, empty: true } : { text, empty: false };
}

// Only the records whose history carries the ERP entry list their titles: on every other record
// they are empty static nodes, and listing them would change what those records always showed.
function visitTitle(node: CompiledStaticNode, label: string, walk: Walk): void {
  if (!walk.deviceTitles || !node.visible) return;
  const mark = walk.marks.values[node.id] ?? null;
  const value = titleValue(node.text);
  pushEntry(walk, { key: node.id, value, tone: mark?.kind ?? null, before: null, label, wide: value.text.length > WIDE_VALUE_LENGTH });
  if (!mark) return;
  walk.changes.push({ key: node.id, tone: mark.kind, context: null, label, before: null, now: value, cells: [], targets: [node.id] });
}

function titleLabel(node: CompiledStaticNode): string | null {
  const label = node.element.properties.label?.trim();
  return label ? label : null;
}

function titleFallback(node: CompiledStaticNode): string {
  const name = node.element.properties.name.trim();
  return name ? name : node.id;
}

function staticText(node: CompiledNode | null): string | null {
  if (node?.kind !== 'static' || !node.visible || node.type !== 'FIXED_TEXT') return null;
  const text = node.text.trim();
  return text ? text : null;
}

function headingOf(text: string | null): string | null {
  return text && text.length <= HEADING_MAX_LENGTH ? text : null;
}

// A row is named by the value of its first visible cell, which the column label would only repeat
// ("LOTE Lote 10005"); when that cell is the change, the old name would point at a value the row no
// longer has, so the number names it instead.
function rowNames(cells: RowCell[], number: number) {
  const numbered = `Linha ${number}`;
  const first = cells[0];
  const firstValue = first ? fieldValue(first.node, first.node.value) : null;
  const name = !firstValue || firstValue.empty ? numbered : firstValue.text;
  return { name, numbered, firstColumn: first?.columnIndex ?? -1, namedByFirst: name !== numbered };
}

interface TableRowCells {
  key: string;
  number: number;
  isNew: boolean;
  cells: RowCell[];
}

type LabelOf = (node: CompiledFieldNode) => string;

function extraRowCells(tableId: string, row: CompiledExtraRow, walk: Walk): TableRowCells {
  const isNew = walk.marks.newRows[tableId]?.includes(row.rowIndex) ?? false;
  const marks = isNew ? {} : (walk.marks.cells[tableId]?.[row.rowIndex] ?? {});
  const cells = row.cells.flatMap((cell) =>
    cell.node?.state.visible ? [{ columnIndex: cell.columnIndex, node: cell.node, mark: marks[cell.columnIndex] ?? null }] : [],
  );
  // The model row is line 1 on the paper, so the added rows count from 2.
  return { key: `${tableId}:row:${row.rowIndex}`, number: row.rowIndex + 2, isNew, cells };
}

function newRowChange(row: TableRowCells, names: ReturnType<typeof rowNames>, labelOf: LabelOf): FillLogsChangeItem {
  const listed = names.namedByFirst ? row.cells.slice(1) : row.cells;
  return {
    key: row.key,
    tone: 'filled',
    context: null,
    label: `Linha nova: ${names.name}`,
    before: null,
    now: null,
    cells: listed.map((cell) => ({
      columnIndex: cell.columnIndex,
      label: labelOf(cell.node),
      value: fieldValue(cell.node, cell.node.value),
    })),
    targets: row.cells.map((cell) => cell.node.id),
  };
}

function recordRowChanges(row: TableRowCells, labelOf: LabelOf, header: Map<number, string>, walk: Walk): void {
  const names = rowNames(row.cells, row.number);
  for (const { columnIndex, node, mark } of row.cells) {
    const label = labelOf(node);
    if (!header.has(columnIndex)) header.set(columnIndex, label);
    if (mark) pushChange(walk, node, mark, label, columnIndex === names.firstColumn ? names.numbered : names.name);
  }
  if (row.isNew) walk.changes.push(newRowChange(row, names, labelOf));
}

function rowReadings(row: TableRowCells, columns: number[], walk: Walk): Array<FillLogsReading | null> {
  const byColumn = new Map(row.cells.map((cell) => [cell.columnIndex, cell]));
  return columns.map((column) => {
    const cell = byColumn.get(column);
    if (!cell) return null;
    const reading = readingOf(cell.node, cell.mark, walk);
    return row.isNew ? { ...reading, tone: 'filled' } : reading;
  });
}

function tableBlock(table: CompiledTableNode, model: RowCell[], walk: Walk, labels: Map<string, string>): void {
  const labelOf: LabelOf = (node) => labels.get(node.element.id) ?? ownLabel(node) ?? fallbackLabel(node);
  const rows = [
    { key: `${table.id}:row:model`, number: 1, isNew: false, cells: model },
    ...table.extraRows.map((row) => extraRowCells(table.id, row, walk)),
  ].filter((row) => row.cells.length > 0);
  const columns = [...new Set(rows.flatMap((row) => row.cells.map((cell) => cell.columnIndex)))].sort((a, b) => a - b);
  if (columns.length === 0) return;

  const header = new Map<number, string>();
  const tableRows: FillLogsTableRow[] = rows.map((row) => {
    recordRowChanges(row, labelOf, header, walk);
    return { key: row.key, number: row.number, isNew: row.isNew, cells: rowReadings(row, columns, walk) };
  });
  pushBlock(walk, { kind: 'table', key: table.id, columns: columns.map((column) => header.get(column) ?? ''), rows: tableRows });
}

interface TableWalk {
  table: CompiledTableNode;
  walk: Walk;
  above: Map<number, string>;
  labels: Map<string, string>;
  model: RowCell[];
  inModelRow: boolean;
  left: string | null;
}

// Most matrices leave the field label blank and name the field with a fixed text in the cell to its
// left or in the row above, so the label a reader sees on the paper is found there, not on the field.
function visitTableField(node: CompiledFieldNode, column: number, state: TableWalk): void {
  const label = ownLabel(node) ?? state.left ?? state.above.get(column) ?? fallbackLabel(node);
  state.labels.set(node.element.id, label);
  if (!state.inModelRow) visitField(node, label, state.walk);
  else if (node.state.visible) state.model.push({ columnIndex: column, node, mark: state.walk.marks.values[node.id] ?? null });
}

function visitTableCell(cell: CompiledCell, state: TableWalk): void {
  const node = cell.hidden ? null : cell.node;
  const text = staticText(node);
  const heading = cell.colSpan >= state.table.columns ? headingOf(text) : null;
  if (heading) openSection(state.walk, heading);
  else if (text) for (let column = cell.colIndex; column < cell.colIndex + cell.colSpan; column += 1) state.above.set(column, text);
  if (node?.kind === 'field') visitTableField(node, cell.colIndex, state);
  else if (isDeviceTitle(node) && !state.inModelRow) {
    visitTitle(node, titleLabel(node) ?? state.left ?? state.above.get(cell.colIndex) ?? titleFallback(node), state.walk);
  }
  else if (node?.kind === 'table' && isNodeShown(node)) visitTable(node, state.walk);
  state.left = heading ? null : text;
}

function visitTable(table: CompiledTableNode, walk: Walk): void {
  const state: TableWalk = { table, walk, above: new Map(), labels: new Map(), model: [], inModelRow: false, left: null };
  table.cells.forEach((row, rowIndex) => {
    state.left = null;
    state.inModelRow = table.isMultiple && rowIndex === table.cells.length - 1;
    row.forEach((cell) => visitTableCell(cell, state));
  });
  if (table.isMultiple) tableBlock(table, state.model, walk, state.labels);
}

// Walks the photo the paper draws, in its order, so a field the rules hide never reaches the text
// either.
export function buildFillLogsText(
  form: CompiledForm,
  marks: ResponseMarks,
  authorOf: AuthorOf,
  options: FillLogsTextOptions = {},
): FillLogsText {
  const walk: Walk = { marks, authorOf, deviceTitles: options.deviceTitles === true, changes: [], sections: [] };
  for (const node of form.nodes) {
    if (!isNodeShown(node)) continue;
    const heading = headingOf(staticText(node));
    if (heading) openSection(walk, heading);
    else if (node.kind === 'field') visitField(node, ownLabel(node) ?? fallbackLabel(node), walk);
    else if (node.kind === 'table') visitTable(node, walk);
    else if (isDeviceTitle(node)) visitTitle(node, titleLabel(node) ?? titleFallback(node), walk);
  }
  return {
    changes: walk.changes,
    sections: walk.sections.filter((section) => section.blocks.length > 0),
  };
}

export function changeCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'mudança' : 'mudanças'}`;
}

export interface ChangeCounts {
  filled: number;
  corrected: number;
}

export function countChanges(changes: readonly FillLogsChangeItem[]): ChangeCounts {
  const filled = changes.filter((change) => change.tone === 'filled').length;
  return { filled, corrected: changes.length - filled };
}

// Counts what buildFillLogsText would list without compiling the photo, which copies every added
// row and made a long history grow with responses times rows. Must agree with the walk above: a
// field counts where it is visible, a new row once when one of its columns is, and a cell of a new
// row never on its own. fill-logs-text.test.ts checks both against each other.
export function countVisibleChanges(
  slots: ChangeSlots,
  isVisible: (fieldId: string) => boolean,
  marks: ResponseMarks
): ChangeCounts {
  const counts: ChangeCounts = { filled: 0, corrected: 0 };
  for (const fieldId of slots.fieldIds) if (isVisible(fieldId)) addMark(counts, marks.values[fieldId]);
  for (const table of slots.tables) {
    const columns = table.columns.filter((column) => isVisible(column.fieldId));
    if (columns.length > 0) addTableMarks(counts, table.tableId, columns, marks);
  }
  return counts;
}

function addMark(counts: ChangeCounts, mark: ChangeMark | undefined): void {
  if (mark) counts[mark.kind] += 1;
}

function addTableMarks(
  counts: ChangeCounts,
  tableId: string,
  columns: readonly ChangeSlotColumn[],
  marks: ResponseMarks
): void {
  const newRows = marks.newRows[tableId] ?? [];
  counts.filled += newRows.length;
  for (const [rowKey, cells] of Object.entries(marks.cells[tableId] ?? {})) {
    if (!newRows.includes(Number(rowKey))) for (const column of columns) addMark(counts, cells[column.columnIndex]);
  }
}

export function changeChipLabel(count: number, word: 'preenchido' | CorrectedWord): string {
  const plural = `${word}s`;
  return `${count} ${count === 1 ? word : plural}`;
}

export function changeBreakdown(changes: readonly FillLogsChangeItem[], correctedWord: CorrectedWord): string {
  const { filled, corrected } = countChanges(changes);
  const parts: string[] = [];
  if (filled > 0) parts.push(changeChipLabel(filled, 'preenchido'));
  if (corrected > 0) parts.push(changeChipLabel(corrected, correctedWord));
  return parts.join(' e ');
}
